import { useEffect, useMemo, useState } from 'react';
import { createFileRoute } from '@tanstack/react-router';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import {
  Palette, Type, Layout as LayoutIcon, Image as ImgIcon, Code2, Sliders, LayoutTemplate,
  Check, Copy, Trash2, Plus, Eye, Save, RotateCcw, Monitor, Tablet, Smartphone,
} from 'lucide-react';
import { toast } from 'sonner';
import { api, unwrap } from '@/lib/api';
import { config } from '@/lib/config';
import { Button } from '@/components/ui/Button';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '@/components/ui/Card';
import { Input, Label, Textarea } from '@/components/ui/Input';
import { Badge } from '@/components/ui/Badge';
import { Tabs, TabsList, TabsTrigger, TabsContent } from '@/components/ui/Tabs';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from '@/components/ui/Dialog';
import { MediaPicker } from '@/features/media/MediaPicker';
import { PackCustomizer } from '@/features/theme/Customizer';
import { DemoImporter } from '@/features/theme/DemoImporter';
import { LivePreview } from '@/features/theme/LivePreview';
import { cn } from '@/lib/cn';

interface Theme {
  id: string;
  name: string;
  slug: string;
  pack: string | null;
  description: string | null;
  is_active: boolean;
  is_preset: boolean;
  colors: Record<string, string> | null;
  typography: Record<string, string> | null;
  layout: Record<string, string> | null;
  branding: Record<string, string> | null;
  pack_options: Record<string, string> | null;
  custom_css: string | null;
  preview_image: string | null;
}

export const Route = createFileRoute('/_app/theme')({
  component: ThemeStudio,
});

const FRONTEND_URL = config.frontendUrl;

const COLOR_FIELDS: Array<{ key: string; label: string }> = [
  { key: 'primary', label: 'Primario' },
  { key: 'primary-fg', label: 'Primario (texto)' },
  { key: 'secondary', label: 'Secundario' },
  { key: 'secondary-fg', label: 'Secundario (texto)' },
  { key: 'accent', label: 'Acento' },
  { key: 'bg', label: 'Fondo' },
  { key: 'bg-soft', label: 'Fondo suave' },
  { key: 'fg', label: 'Texto principal' },
  { key: 'muted', label: 'Texto secundario' },
  { key: 'border', label: 'Bordes' },
  { key: 'success', label: 'Éxito' },
  { key: 'danger', label: 'Error' },
  { key: 'warning', label: 'Aviso' },
];

const TYPOGRAPHY_FIELDS: Array<{ key: string; label: string; placeholder?: string }> = [
  { key: 'sans', label: 'Fuente principal (sans)', placeholder: "'Inter', system-ui, sans-serif" },
  { key: 'serif', label: 'Fuente serifa', placeholder: "'Source Serif Pro', Georgia, serif" },
  { key: 'display', label: 'Fuente de títulos (display)', placeholder: "'Inter Tight', sans-serif" },
  { key: 'mono', label: 'Fuente monoespaciada', placeholder: "'JetBrains Mono', monospace" },
  { key: 'scale-base', label: 'Tamaño base', placeholder: '16px' },
];

const LAYOUT_FIELDS: Array<{ key: string; label: string; placeholder?: string }> = [
  { key: 'radius', label: 'Radio de bordes', placeholder: '8px' },
  { key: 'radius-lg', label: 'Radio grande', placeholder: '14px' },
  { key: 'container-max', label: 'Ancho máx. del contenedor', placeholder: '1280px' },
  { key: 'gap', label: 'Espaciado de grids', placeholder: '24px' },
];

function ThemeStudio() {
  const qc = useQueryClient();
  const { data: themes } = useQuery({
    queryKey: ['themes'],
    queryFn: () => unwrap<Theme[]>(api.get('/themes')),
  });

  const [editingId, setEditingId] = useState<string | null>(null);
  const editing = useMemo(() => themes?.find((t) => t.id === editingId) ?? null, [themes, editingId]);

  // Autoselect active on first load
  useEffect(() => {
    if (themes && !editingId) {
      const active = themes.find((t) => t.is_active) ?? themes[0];
      if (active) setEditingId(active.id);
    }
  }, [themes, editingId]);

  return (
    <div className="mx-auto max-w-7xl p-6 md:p-8">
      <header className="mb-6 flex flex-col gap-3 md:flex-row md:items-end md:justify-between">
        <div>
          <h1 className="text-3xl font-bold tracking-tight">Tema visual</h1>
          <p className="text-sm text-neutral-500">
            Cambia colores, tipografías y branding de toda la web sin tocar código.
          </p>
        </div>
      </header>

      <div className="grid gap-6 lg:grid-cols-[320px_1fr]">
        <ThemeList themes={themes ?? []} editingId={editingId} setEditingId={setEditingId} qc={qc} />
        {editing ? (
          <ThemeEditor key={editing.id} theme={editing} qc={qc} />
        ) : (
          <Card>
            <CardContent className="py-16 text-center text-sm text-neutral-500">
              Selecciona un tema para editarlo o crea uno nuevo.
            </CardContent>
          </Card>
        )}
      </div>
    </div>
  );
}

function ThemeList({
  themes, editingId, setEditingId, qc,
}: {
  themes: Theme[];
  editingId: string | null;
  setEditingId: (id: string) => void;
  qc: ReturnType<typeof useQueryClient>;
}) {
  const activate = useMutation({
    mutationFn: (id: string) => api.post(`/themes/${id}/activate`),
    onSuccess: () => {
      toast.success('Tema activado');
      void qc.invalidateQueries({ queryKey: ['themes'] });
    },
  });

  const duplicate = useMutation({
    mutationFn: (id: string) => unwrap<Theme>(api.post(`/themes/${id}/duplicate`)),
    onSuccess: (t) => {
      toast.success('Tema duplicado');
      void qc.invalidateQueries({ queryKey: ['themes'] });
      setEditingId(t.id);
    },
  });

  const remove = useMutation({
    mutationFn: (id: string) => api.delete(`/themes/${id}`),
    onSuccess: () => {
      toast.success('Tema eliminado');
      void qc.invalidateQueries({ queryKey: ['themes'] });
    },
    onError: (e: any) => toast.error(e?.response?.data?.message ?? 'No se pudo eliminar'),
  });

  return (
    <aside className="space-y-3">
      <Card>
        <CardHeader>
          <CardTitle className="text-sm">Mis temas</CardTitle>
          <CardDescription>{themes.length} disponibles.</CardDescription>
        </CardHeader>
        <CardContent className="space-y-1.5 p-2">
          {themes.map((t) => (
            <div
              key={t.id}
              className={cn(
                'group rounded-md border p-2.5 transition-all',
                editingId === t.id
                  ? 'border-neutral-900 ring-2 ring-neutral-900/10'
                  : 'border-neutral-200 hover:border-neutral-400',
              )}
            >
              <button
                onClick={() => setEditingId(t.id)}
                className="block w-full text-left"
              >
                <div className="mb-2 flex items-center gap-1.5">
                  {(t.colors ?? {}) && (
                    <>
                      <ColorSwatch color={t.colors?.primary} size="md" />
                      <ColorSwatch color={t.colors?.secondary} size="md" />
                      <ColorSwatch color={t.colors?.accent} size="md" />
                      <ColorSwatch color={t.colors?.bg} size="md" />
                    </>
                  )}
                </div>
                <div className="flex items-center gap-1.5">
                  <span className="text-sm font-medium">{t.name}</span>
                  {t.is_active && <Badge variant="default" className="bg-green-600 text-[10px]"><Check className="size-3" /> Activo</Badge>}
                  {t.is_preset && <Badge variant="outline" className="text-[10px]">Preset</Badge>}
                </div>
                {t.description && (
                  <p className="mt-1 line-clamp-2 text-xs text-neutral-500">{t.description}</p>
                )}
              </button>
              <div className="mt-2 flex gap-1 opacity-0 transition-opacity group-hover:opacity-100">
                <PreviewButton theme={t} />
                {!t.is_active && (
                  <Button size="sm" variant="outline" className="h-7 text-xs flex-1" onClick={() => activate.mutate(t.id)}>
                    Activar
                  </Button>
                )}
                <Button size="sm" variant="ghost" className="h-7 px-2" onClick={() => duplicate.mutate(t.id)} title="Duplicar">
                  <Copy className="size-3" />
                </Button>
                {!t.is_active && !t.is_preset && (
                  <Button
                    size="sm"
                    variant="ghost"
                    className="h-7 px-2 text-red-600 hover:bg-red-50"
                    title="Eliminar"
                    onClick={() => {
                      if (confirm(`¿Eliminar tema "${t.name}"?`)) remove.mutate(t.id);
                    }}
                  >
                    <Trash2 className="size-3" />
                  </Button>
                )}
              </div>
            </div>
          ))}
        </CardContent>
      </Card>
      <NewThemeButton qc={qc} setEditingId={setEditingId} />
    </aside>
  );
}

function NewThemeButton({ qc, setEditingId }: { qc: ReturnType<typeof useQueryClient>; setEditingId: (id: string) => void }) {
  const [open, setOpen] = useState(false);
  const [name, setName] = useState('');
  const create = useMutation({
    mutationFn: () => unwrap<Theme>(api.post('/themes', {
      name,
      colors: { primary: '#0ea5e9', 'primary-fg': '#ffffff', bg: '#ffffff', fg: '#0f172a' },
      typography: { sans: "'Inter', system-ui, sans-serif" },
      layout: { radius: '8px', 'container-max': '1280px' },
    })),
    onSuccess: (t) => {
      toast.success('Tema creado');
      void qc.invalidateQueries({ queryKey: ['themes'] });
      setEditingId(t.id);
      setOpen(false);
      setName('');
    },
  });

  return (
    <>
      <Button variant="outline" className="w-full" onClick={() => setOpen(true)}>
        <Plus className="size-4" /> Nuevo tema
      </Button>
      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Crear tema desde cero</DialogTitle>
            <DialogDescription>Más cómodo si duplicas uno existente desde la lista.</DialogDescription>
          </DialogHeader>
          <div className="space-y-3">
            <div>
              <Label>Nombre</Label>
              <Input value={name} onChange={(e) => setName(e.target.value)} placeholder="Ej: Cliente XYZ - Brand 2026" />
            </div>
            <Button
              className="w-full"
              disabled={!name || create.isPending}
              onClick={() => create.mutate()}
            >
              Crear y editar
            </Button>
          </div>
        </DialogContent>
      </Dialog>
    </>
  );
}

function ThemeEditor({ theme, qc }: { theme: Theme; qc: ReturnType<typeof useQueryClient> }) {
  const [name, setName] = useState(theme.name);
  const [description, setDescription] = useState(theme.description ?? '');
  const [colors, setColors] = useState<Record<string, string>>(theme.colors ?? {});
  const [typography, setTypography] = useState<Record<string, string>>(theme.typography ?? {});
  const [layout, setLayout] = useState<Record<string, string>>(theme.layout ?? {});
  const [branding, setBranding] = useState<Record<string, string>>(theme.branding ?? {});
  const [packOptions, setPackOptions] = useState<Record<string, string>>(theme.pack_options ?? {});
  const [customCss, setCustomCss] = useState(theme.custom_css ?? '');

  function reset() {
    setName(theme.name);
    setDescription(theme.description ?? '');
    setColors(theme.colors ?? {});
    setTypography(theme.typography ?? {});
    setLayout(theme.layout ?? {});
    setBranding(theme.branding ?? {});
    setPackOptions(theme.pack_options ?? {});
    setCustomCss(theme.custom_css ?? '');
  }

  const save = useMutation({
    mutationFn: () => unwrap<Theme>(api.put(`/themes/${theme.id}`, {
      name, description: description || null, colors, typography, layout, branding,
      pack_options: packOptions,
      custom_css: customCss || null,
    })),
    onSuccess: () => {
      toast.success('Tema guardado');
      void qc.invalidateQueries({ queryKey: ['themes'] });
    },
    onError: () => toast.error('No se pudo guardar'),
  });

  return (
    <div>
      <Card>
        <CardHeader>
          <div className="flex flex-wrap items-center justify-between gap-3">
            <div>
              <CardTitle>Editor de tema</CardTitle>
              <CardDescription>{theme.is_active ? 'Cambios afectan al sitio público.' : 'Tema inactivo. Actívalo para publicarlo.'}</CardDescription>
            </div>
            <div className="flex gap-2">
              <Button variant="outline" size="sm" onClick={reset}><RotateCcw className="size-3.5" /> Descartar</Button>
              <Button size="sm" disabled={save.isPending} onClick={() => save.mutate()}><Save className="size-3.5" /> {save.isPending ? 'Guardando…' : 'Guardar'}</Button>
            </div>
          </div>
        </CardHeader>
        <CardContent>
          <Tabs defaultValue="customize">
            <TabsList className="mb-4">
              <TabsTrigger value="customize"><Sliders className="size-3.5 mr-1.5" /> Personalizar</TabsTrigger>
              <TabsTrigger value="demos"><LayoutTemplate className="size-3.5 mr-1.5" /> Demos</TabsTrigger>
              <TabsTrigger value="colors"><Palette className="size-3.5 mr-1.5" /> Colores</TabsTrigger>
              <TabsTrigger value="typography"><Type className="size-3.5 mr-1.5" /> Tipografía</TabsTrigger>
              <TabsTrigger value="layout"><LayoutIcon className="size-3.5 mr-1.5" /> Layout</TabsTrigger>
              <TabsTrigger value="branding"><ImgIcon className="size-3.5 mr-1.5" /> Branding</TabsTrigger>
              <TabsTrigger value="meta">Metadatos</TabsTrigger>
              <TabsTrigger value="custom"><Code2 className="size-3.5 mr-1.5" /> CSS custom</TabsTrigger>
            </TabsList>

            <TabsContent value="customize">
              <PackCustomizer
                packSlug={theme.pack}
                themeSlug={theme.slug}
                values={packOptions}
                onChange={setPackOptions}
                frontendUrl={FRONTEND_URL}
              />
            </TabsContent>

            <TabsContent value="demos">
              <DemoImporter packSlug={theme.pack} themeSlug={theme.slug} frontendUrl={FRONTEND_URL} />
            </TabsContent>

            <TabsContent value="colors">
              <div className="grid gap-3 md:grid-cols-2">
                {COLOR_FIELDS.map((c) => (
                  <ColorRow
                    key={c.key}
                    label={c.label}
                    value={colors[c.key] ?? ''}
                    onChange={(v) => setColors((p) => ({ ...p, [c.key]: v }))}
                  />
                ))}
              </div>
            </TabsContent>

            <TabsContent value="typography">
              <div className="space-y-3">
                {TYPOGRAPHY_FIELDS.map((f) => (
                  <div key={f.key}>
                    <Label className="text-xs">{f.label}</Label>
                    <Input
                      value={typography[f.key] ?? ''}
                      onChange={(e) => setTypography((p) => ({ ...p, [f.key]: e.target.value }))}
                      placeholder={f.placeholder}
                      className="font-mono text-xs"
                    />
                  </div>
                ))}
                <p className="text-xs text-neutral-500">
                  Tip: si usas una fuente de Google entre comillas (ej: <code>'Manrope'</code>), se cargará automáticamente.
                </p>
              </div>
            </TabsContent>

            <TabsContent value="layout">
              <div className="grid gap-3 md:grid-cols-2">
                {LAYOUT_FIELDS.map((f) => (
                  <div key={f.key}>
                    <Label className="text-xs">{f.label}</Label>
                    <Input
                      value={layout[f.key] ?? ''}
                      onChange={(e) => setLayout((p) => ({ ...p, [f.key]: e.target.value }))}
                      placeholder={f.placeholder}
                    />
                  </div>
                ))}
              </div>
            </TabsContent>

            <TabsContent value="branding">
              <div className="space-y-3">
                <ImageField
                  label="Logo principal"
                  value={branding.logo_url ?? ''}
                  onChange={(v) => setBranding((p) => ({ ...p, logo_url: v }))}
                />
                <ImageField
                  label="Logo modo oscuro"
                  value={branding.logo_dark_url ?? ''}
                  onChange={(v) => setBranding((p) => ({ ...p, logo_dark_url: v }))}
                />
                <ImageField
                  label="Favicon"
                  value={branding.favicon_url ?? ''}
                  onChange={(v) => setBranding((p) => ({ ...p, favicon_url: v }))}
                />
                <ImageField
                  label="Imagen Open Graph por defecto"
                  value={branding.og_default_url ?? ''}
                  onChange={(v) => setBranding((p) => ({ ...p, og_default_url: v }))}
                />
              </div>
            </TabsContent>

            <TabsContent value="meta">
              <div className="space-y-3">
                <div>
                  <Label>Nombre del tema</Label>
                  <Input value={name} onChange={(e) => setName(e.target.value)} />
                </div>
                <div>
                  <Label>Descripción</Label>
                  <Textarea value={description} onChange={(e) => setDescription(e.target.value)} rows={3} />
                </div>
              </div>
            </TabsContent>

            <TabsContent value="custom">
              <Label>CSS personalizado (avanzado)</Label>
              <Textarea
                value={customCss}
                onChange={(e) => setCustomCss(e.target.value)}
                rows={14}
                className="font-mono text-xs"
                placeholder=":root { --color-x: red; } .v5-hero { background: var(--color-x); }"
              />
              <p className="mt-1.5 text-xs text-neutral-500">Este CSS se inyecta al final del &lt;style&gt; del tema. Úsalo solo para overrides muy específicos.</p>
            </TabsContent>
          </Tabs>
        </CardContent>
      </Card>

      {/* Live preview of the real site (unsaved edits included) */}
      <Card className="mt-6">
        <CardHeader>
          <CardTitle className="text-base">Vista previa en vivo</CardTitle>
          <CardDescription>
            El sitio real con este tema. Refleja tus cambios sin guardar (colores, tipografía, layout y opciones).
          </CardDescription>
        </CardHeader>
        <CardContent>
          <LivePreview
            themeSlug={theme.slug}
            frontendUrl={FRONTEND_URL}
            colors={colors}
            typography={typography}
            layout={layout}
            packOptions={packOptions}
          />
        </CardContent>
      </Card>
    </div>
  );
}

function PreviewButton({ theme }: { theme: Theme }) {
  const [open, setOpen] = useState(false);
  const [vp, setVp] = useState<'desktop' | 'tablet' | 'mobile'>('desktop');
  const widths = { desktop: '100%', tablet: '820px', mobile: '390px' } as const;
  const src = `${FRONTEND_URL}/preview/${theme.slug}`;

  return (
    <>
      <Button
        size="sm"
        variant="outline"
        className="h-7 flex-1 text-xs"
        onClick={(e) => {
          e.stopPropagation();
          setOpen(true);
        }}
        title="Vista previa real (sin activar)"
      >
        <Eye className="size-3" /> Vista previa
      </Button>
      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent size="full" className="flex flex-col">
          <DialogHeader>
            <div className="flex flex-wrap items-center justify-between gap-3 pr-8">
              <div>
                <DialogTitle>Vista previa — {theme.name}</DialogTitle>
                <DialogDescription>
                  Diseño real del tema sin activarlo.{' '}
                  <a href={src} target="_blank" rel="noopener noreferrer" className="underline">
                    Abrir en pestaña ↗
                  </a>
                </DialogDescription>
              </div>
              <div className="flex items-center rounded-md border border-neutral-200">
                <VpBtn active={vp === 'desktop'} onClick={() => setVp('desktop')}><Monitor className="size-3.5" /></VpBtn>
                <VpBtn active={vp === 'tablet'} onClick={() => setVp('tablet')}><Tablet className="size-3.5" /></VpBtn>
                <VpBtn active={vp === 'mobile'} onClick={() => setVp('mobile')}><Smartphone className="size-3.5" /></VpBtn>
              </div>
            </div>
          </DialogHeader>
          <div className="min-h-0 flex-1 overflow-auto rounded-lg bg-neutral-100 p-3">
            <iframe
              key={`${theme.slug}-${vp}`}
              src={src}
              title={`Vista previa de ${theme.name}`}
              className="mx-auto block h-full border-0 bg-white shadow-sm transition-all"
              style={{ width: widths[vp] }}
            />
          </div>
        </DialogContent>
      </Dialog>
    </>
  );
}

function ColorRow({ label, value, onChange }: { label: string; value: string; onChange: (v: string) => void }) {
  return (
    <div className="flex items-center gap-2">
      <input
        type="color"
        value={value || '#000000'}
        onChange={(e) => onChange(e.target.value)}
        className="h-9 w-12 cursor-pointer rounded border border-neutral-200"
      />
      <div className="flex-1">
        <Label className="text-xs text-neutral-500">{label}</Label>
        <Input
          value={value}
          onChange={(e) => onChange(e.target.value)}
          placeholder="#000000"
          className="h-8 font-mono text-xs"
        />
      </div>
    </div>
  );
}

function ImageField({ label, value, onChange }: { label: string; value: string; onChange: (v: string) => void }) {
  const [open, setOpen] = useState(false);
  return (
    <div>
      <Label className="text-xs">{label}</Label>
      <div className="flex items-start gap-2">
        {value ? (
          <div className="relative size-20 shrink-0 overflow-hidden rounded border border-neutral-200 bg-neutral-50">
            <img src={value} alt="" className="size-full object-contain" />
            <button
              type="button"
              onClick={() => onChange('')}
              className="absolute right-1 top-1 rounded bg-white/90 p-0.5 text-red-600"
            >
              <Trash2 className="size-3" />
            </button>
          </div>
        ) : (
          <button
            type="button"
            onClick={() => setOpen(true)}
            className="grid size-20 shrink-0 place-items-center rounded border border-dashed border-neutral-300 text-neutral-400 hover:border-neutral-500"
          >
            <ImgIcon className="size-5" />
          </button>
        )}
        <div className="flex-1 space-y-1.5">
          <Input value={value} onChange={(e) => onChange(e.target.value)} placeholder="URL externa o /assets/…" className="text-xs" />
          <Button type="button" size="sm" variant="outline" className="w-full" onClick={() => setOpen(true)}>
            Elegir de la biblioteca
          </Button>
        </div>
      </div>
      <MediaPicker open={open} onClose={() => setOpen(false)} onSelect={(url) => onChange(url)} accept="image" />
    </div>
  );
}

function ColorSwatch({ color, size = 'sm' }: { color?: string; size?: 'sm' | 'md' }) {
  if (!color) return null;
  return (
    <div
      className={cn('rounded-full border border-neutral-200', size === 'md' ? 'size-4' : 'size-3')}
      style={{ backgroundColor: color }}
    />
  );
}

function VpBtn({ active, onClick, children }: { active: boolean; onClick: () => void; children: React.ReactNode }) {
  return (
    <button
      type="button"
      onClick={onClick}
      className={cn(
        'grid size-8 place-items-center transition-colors first:rounded-l-md last:rounded-r-md',
        active ? 'bg-neutral-900 text-white' : 'text-neutral-600 hover:bg-neutral-100',
      )}
    >
      {children}
    </button>
  );
}

